import React, { useState } from 'react';
import { ChevronDown, LogOut, Menu, X } from 'lucide-react';

/**
 * Light-theme vertical sidebar used by the vendor portal shell.
 *
 * Props:
 *  - vendor: { vendor_name, email, logo_url? }
 *  - sections: [{ label, items: [{ id, label, icon: LucideComponent, badge?: number }] }]
 *  - activeId: string of the currently-selected item.id
 *  - onChange(id): callback
 *  - onLogout(): callback
 */
export const VendorSidebar = ({ vendor, sections, activeId, onChange, onLogout }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  return (
    <>
      {/* Mobile menu toggle (top-left) */}
      <button
        type="button"
        onClick={() => setMobileOpen((o) => !o)}
        className="md:hidden fixed top-3 left-3 z-50 h-9 w-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center"
        data-testid="vendor-sidebar-toggle"
      >
        {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
      </button>

      <aside
        className={`fixed left-0 top-0 h-screen w-[260px] bg-white border-r border-gray-200 flex flex-col z-40 transform transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        data-testid="vendor-sidebar"
      >
        {/* Brand block */}
        <div className="px-5 py-5 flex items-center gap-3 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-violet-600 text-white flex items-center justify-center font-semibold shrink-0 overflow-hidden">
            {vendor?.logo_url ? (
              <img src={vendor.logo_url} alt={vendor.vendor_name} className="w-full h-full object-cover" />
            ) : (
              (vendor?.vendor_name || 'V').slice(0, 1).toUpperCase()
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate" data-testid="vendor-brand-name">
              {vendor?.vendor_name || 'Vendor'}
            </div>
            <div className="text-[11px] text-gray-500 truncate">Seller Panel</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto vp-scroll px-2 py-4">
          {sections.map((section) => (
            <div key={section.label} className="mb-4">
              <div className="px-3 mb-2 text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                {section.label}
              </div>
              <ul className="space-y-0.5">
                {section.items.map((it) => {
                  const Icon = it.icon;
                  const active = activeId === it.id;
                  return (
                    <li key={it.id}>
                      <button
                        type="button"
                        onClick={() => { onChange(it.id); setMobileOpen(false); }}
                        className={`relative w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          active
                            ? 'text-violet-700 bg-violet-50'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                        data-testid={`vendor-nav-${it.id}`}
                      >
                        {active && (
                          <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-violet-600 rounded-r-full" />
                        )}
                        {Icon && <Icon className="w-4 h-4 shrink-0" />}
                        <span className="flex-1 truncate">{it.label}</span>
                        {typeof it.badge === 'number' && it.badge > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold">
                            {it.badge}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Profile dropdown */}
        <div className="border-t border-gray-100 relative">
          <button
            type="button"
            onClick={() => setProfileOpen((o) => !o)}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
            data-testid="vendor-profile-toggle"
          >
            <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center font-semibold text-sm">
              {(vendor?.vendor_name || 'V').slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-sm font-medium text-gray-900 truncate">{vendor?.vendor_name}</div>
              <div className="text-[11px] text-gray-500 truncate">{vendor?.email}</div>
            </div>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
          </button>
          {profileOpen && (
            <div
              className="absolute bottom-full left-2 right-2 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
              data-testid="vendor-profile-menu"
            >
              <button
                type="button"
                onClick={() => { setProfileOpen(false); onChange('profile'); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                data-testid="vendor-profile-link"
              >
                Profile & Settings
              </button>
              <button
                type="button"
                onClick={() => { setProfileOpen(false); onLogout(); }}
                className="w-full text-left px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                data-testid="vendor-sidebar-logout"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default VendorSidebar;
